/**
 * src/components/capture/ScopeConfirmSheet.jsx -- v24.0 Knowledge Vault, slice E.
 *
 * The ALWAYS-shown one-screen scope confirm (locked design). captureAndFile
 * pre-fills it from the classifier and the current view scope; the user can
 * correct EVERY field before filing. Confirm is disabled until at least one
 * scope anchor (project | vessel | deal) is chosen -- the invariant that keeps a
 * capture from ever being orphaned. needs_confirm drives a "please check" hint,
 * never whether the sheet appears.
 *
 * Mobile-first bottom sheet (375px). Tokens only; 44px touch targets. EN+ES.
 */

import { useState } from 'react';
import { useT } from '../../lib/i18n/I18nContext';

const DOC_TYPES = ['photo', 'pdf', 'spec', 'manual', 'technical_datasheet', 'cnc_drawing', 'cad_drawing', 'construction_drawing', 'warranty', 'invoice', 'note', 'other'];

const inputStyle = {
  minHeight: 44,
  padding: '8px 12px',
  borderRadius: 8,
  border: '1px solid var(--bdr)',
  background: 'var(--glass)',
  color: 'var(--t1)',
  fontSize: 13,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--t2)', marginBottom: 6, display: 'block' };

export default function ScopeConfirmSheet({ prefill = {}, projects = [], vessels = [], deals = [], onConfirm, onCancel }) {
  const { t } = useT();
  const initialScope = prefill.scope || {};
  // A deal that has already been won HAS a project (projects.deal_id is set at
  // promotion). Dropping a file on that deal used to show "Proyecto — ninguno —",
  // which reads as "unmatched" when the link exists and is knowable (TICKET-468).
  // A deal with no project yet is a project-in-waiting, so the empty option is
  // labelled as such rather than as an absence.
  const projectForDeal = initialScope.deal_id
    ? projects.find((p) => p.deal_id === initialScope.deal_id)
    : null;
  const [documentType, setDocumentType] = useState(prefill.document_type || 'other');
  const [projectId, setProjectId] = useState(initialScope.project_id || projectForDeal?.id || '');
  const [vesselId, setVesselId] = useState(initialScope.vessel_id || '');
  const [dealId, setDealId] = useState(initialScope.deal_id || '');
  const [productTypesText, setProductTypesText] = useState(
    Array.isArray(prefill.product_types) ? prefill.product_types.join(', ') : '',
  );

  const hasAnchor = !!(projectId || vesselId || dealId);

  const handleConfirm = () => {
    if (!hasAnchor) return;
    const product_types = productTypesText
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    onConfirm({
      scope: {
        project_id: projectId || null,
        vessel_id: vesselId || null,
        deal_id: dealId || null,
      },
      document_type: documentType,
      product_types,
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('vaultCapture.ariaConfirm')}
      tabIndex={0}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(10,22,40,0.55)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
      // Backdrop dismiss: only when the click/keypress targets the backdrop
      // itself (not a child). Keyboard: Escape closes the sheet.
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onCancel(); }}
    >
      <div
        role="document"
        style={{
          width: '100%',
          maxWidth: 520,
          background: 'var(--glass)',
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          border: '1px solid var(--bdr)',
          borderBottom: 'none',
          padding: '20px 20px calc(20px + env(safe-area-inset-bottom))',
          boxShadow: '0 -8px 32px rgba(10,22,40,0.25)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          maxHeight: '85vh',
          overflowY: 'auto',
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--t1)' }}>{t('vaultCapture.title')}</div>
          <div style={{ fontSize: 12, color: 'var(--t3)', marginTop: 4 }}>
            {t('vaultCapture.subtitle')}
          </div>
        </div>

        {prefill.needs_confirm && (
          <div
            data-testid="capture-confirm-lowconfidence"
            style={{
              fontSize: 12,
              color: 'var(--warn)',
              background: 'rgba(245,158,11,0.12)',
              border: '1px solid var(--bdr)',
              borderRadius: 8,
              padding: '8px 12px',
            }}
          >
            {t('vaultCapture.lowConfidence')}
          </div>
        )}

        <div>
          <label style={labelStyle} htmlFor="capture-doctype">{t('vaultCapture.fieldDocType')}</label>
          <select
            id="capture-doctype"
            data-testid="capture-confirm-doctype"
            value={documentType}
            onChange={(e) => setDocumentType(e.target.value)}
            style={inputStyle}
          >
            {DOC_TYPES.map((dt) => (
              <option key={dt} value={dt}>{dt}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle} htmlFor="capture-project">{t('vaultCapture.fieldProject')}</label>
          <select
            id="capture-project"
            data-testid="capture-confirm-project"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            style={inputStyle}
          >
            <option value="">
              {initialScope.deal_id && !projectForDeal
                ? t('vaultCapture.optionProjectPending')
                : t('vaultCapture.optionNone')}
            </option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.project_name || p.id}</option>
            ))}
          </select>
          {initialScope.deal_id && !projectForDeal && (
            <div data-testid="capture-confirm-project-pending" style={{ fontSize: 11, color: 'var(--t3)', marginTop: 4 }}>
              {t('vaultCapture.projectPendingHint')}
            </div>
          )}
        </div>

        <div>
          <label style={labelStyle} htmlFor="capture-vessel">{t('vaultCapture.fieldVessel')}</label>
          <select
            id="capture-vessel"
            data-testid="capture-confirm-vessel"
            value={vesselId}
            onChange={(e) => setVesselId(e.target.value)}
            style={inputStyle}
          >
            <option value="">{t('vaultCapture.optionNone')}</option>
            {vessels.map((v) => (
              <option key={v.id} value={v.id}>{v.vessel_name || v.id}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle} htmlFor="capture-deal">{t('vaultCapture.fieldDeal')}</label>
          <select
            id="capture-deal"
            data-testid="capture-confirm-deal"
            value={dealId}
            onChange={(e) => setDealId(e.target.value)}
            style={inputStyle}
          >
            <option value="">{t('vaultCapture.optionNone')}</option>
            {deals.map((d) => (
              <option key={d.id} value={d.id}>{d.opportunity || d.id}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={labelStyle} htmlFor="capture-producttypes">{t('vaultCapture.fieldProductTypes')}</label>
          <input
            id="capture-producttypes"
            data-testid="capture-confirm-producttypes"
            type="text"
            value={productTypesText}
            onChange={(e) => setProductTypesText(e.target.value)}
            placeholder={t('vaultCapture.productTypesPlaceholder')}
            style={inputStyle}
          />
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button
            type="button"
            data-testid="capture-confirm-cancel"
            onClick={onCancel}
            style={{
              flex: 1,
              minHeight: 44,
              padding: '8px 12px',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              background: 'var(--glass)',
              color: 'var(--t2)',
              fontSize: 14,
              fontWeight: 600,
              outline: '1px solid var(--bdr)',
            }}
          >
            {t('vaultCapture.cancel')}
          </button>
          <button
            type="button"
            data-testid="capture-confirm-submit"
            onClick={handleConfirm}
            disabled={!hasAnchor}
            style={{
              flex: 2,
              minHeight: 44,
              padding: '8px 12px',
              border: 'none',
              borderRadius: 10,
              cursor: hasAnchor ? 'pointer' : 'default',
              background: 'var(--navy-surface)',
              color: 'var(--glass)',
              fontSize: 14,
              fontWeight: 700,
              opacity: hasAnchor ? 1 : 0.5,
            }}
          >
            {t('vaultCapture.confirm')}
          </button>
        </div>
        {!hasAnchor && (
          <div style={{ fontSize: 11, color: 'var(--t3)', textAlign: 'center', marginTop: -8 }}>
            {t('vaultCapture.anchorHint')}
          </div>
        )}
      </div>
    </div>
  );
}
